import { useRequest } from "@prismicio/editor-support/Suspense"
import { DatePicker, Tooltip, useAlert, useToast } from "@prismicio/editor-ui"
import { useState } from "react"

import {
  getReleasePermissions,
  type Release,
  updateReleaseSchedule,
  updateReleasesListData,
} from "../../app/services/release"

interface ReleaseDateProps {
  release: Release
  size?: "small" | "large"
}

export function ReleaseDate(props: ReleaseDateProps) {
  const { release, size = "small" } = props
  const { updateSchedule, loading } = useUpdateReleaseSchedule()
  const { disabled, tooltip } = useScheduleReleaseButton(release)

  // if the release date year is outside the ISO range, show a disabled date picker
  // we shouldn't experience this in practice, but some test data may have invalid dates
  if (release.timestamp && !isDateYearWithinISORange(release.timestamp)) {
    return (
      <DatePicker
        value={undefined}
        disabled
        triggerSize={size}
        fallback="Invalid date"
        label="release date"
      />
    )
  }

  return (
    <Tooltip
      visible={tooltip.visible}
      title={tooltip.title}
      content={tooltip.content}
    >
      <DatePicker
        value={release.timestamp}
        onSubmit={(date) => updateSchedule(release.id, date)}
        minValue="today"
        minTime="now"
        disabled={disabled || loading}
        triggerSize={size}
        fallback="Not scheduled yet"
        label="release date"
      />
    </Tooltip>
  )
}

function useScheduleReleaseButton(release: Release) {
  const { canPublish: canPublishRelease } = useRequest(getReleasePermissions, [
    release.id,
  ])

  return {
    disabled: !canPublishRelease,
    tooltip: getTooltip({ canPublishRelease }),
  }
}

interface GetTooltipArgs {
  canPublishRelease: boolean
}

function getTooltip(args: GetTooltipArgs) {
  const { canPublishRelease } = args

  if (!canPublishRelease) {
    return {
      visible: true,
      title: "You can't update schedule for this release",
      content:
        "You are missing publishing rights for some of the documents in the release.",
    }
  }

  return {
    visible: false,
    title: "",
    content: "",
  }
}

function useUpdateReleaseSchedule() {
  const [loading, setLoading] = useState(false)
  const { showAlert } = useAlert()
  const { showToast } = useToast()

  async function updateSchedule(releaseId: string, date: Date) {
    setLoading(true)

    try {
      await updateReleaseSchedule(releaseId, date.getTime())
      updateReleasesListData((releases) => {
        // update the specific release list with the new date
        // leave the rest of the list alone
        return releases.map((release) =>
          release.id === releaseId ? { ...release, timestamp: date } : release,
        )
      })

      showToast({
        title: "Release schedule updated",
      })
    } catch {
      showAlert({
        title: "There was an error updating the release schedule.",
        subtitle: "Please try again or contact support.",
        onRetry: () => updateSchedule(releaseId, date),
      })
    } finally {
      setLoading(false)
    }
  }

  return {
    updateSchedule,
    loading,
  }
}

function isDateYearWithinISORange(date: Date) {
  const year = date.getFullYear()
  if (year < 0 || year > 9999) {
    return false
  }

  return true
}
